import { Injectable, inject } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { STORAGE_TYPE, StorageService } from '../services';

const helper = new JwtHelperService();

/**
 * Gerencia tokens JWT com estratégia de armazenamento em camadas:
 *
 * - **Access token** → `sessionStorage` (STORAGE_TYPE.SESSION)
 *   Escopo limitado à aba/janela atual. Reduz a janela de exposição em caso de XSS,
 *   pois o token é descartado ao fechar a aba. Compatível com refresh proativo e interceptor.
 *
 * - **Refresh token** → `localStorage` (STORAGE_TYPE.LOCAL)
 *   Persiste entre sessões do browser para permitir renovação silenciosa após reload.
 *   Trade-off consciente: maior persistência vs. conveniência de "manter conectado".
 *
 * **Migração automática:** tokens legados salvos em `localStorage` sob a chave `token`
 * são movidos para `sessionStorage` na primeira leitura, preservando sessões ativas.
 *
 * **Limitação conhecida:** tokens em storage do browser permanecem acessíveis a scripts
 * na mesma origem (XSS). Mitigação completa exige httpOnly cookies no backend.
 */
@Injectable({ providedIn: 'root' })
export class TokenService {
  private readonly storageService = inject(StorageService);

  private readonly JWT_TOKEN: string = 'token';
  private readonly JWT_REFRESH_TOKEN: string = 'refresh-token';
  private jwtToken: string = '';
  private jwtTokenDecoded: { [key: string]: string } | undefined;

  public get id(): string | undefined {
    this.getToken();
    if (this.jwtTokenDecoded) {
      const { id } = this.jwtTokenDecoded;
      return id;
    }
    return undefined;
  }

  /** User-facing name from JWT claims (name, email, or fallback). */
  public get displayName(): string {
    this.getToken();
    if (!this.jwtTokenDecoded) {
      return 'Usuário';
    }
    return this.jwtTokenDecoded['name'] || this.jwtTokenDecoded['email'] || this.jwtTokenDecoded['sub'] || 'Usuário';
  }

  public get token(): string {
    this.getToken();
    return this.jwtToken;
  }

  public get refreshToken(): string {
    return this.storageService.get<string>(STORAGE_TYPE.LOCAL, this.JWT_REFRESH_TOKEN) ?? '';
  }

  public setToken(token: string): void {
    this.storageService.set(STORAGE_TYPE.SESSION, this.JWT_TOKEN, token);
    this.storageService.remove(STORAGE_TYPE.LOCAL, this.JWT_TOKEN);
    this.jwtToken = token;
    this.decodeToken();
  }

  public setRefreshToken(token: string): void {
    this.storageService.set(STORAGE_TYPE.LOCAL, this.JWT_REFRESH_TOKEN, token);
  }

  private getToken(): void {
    this.migrateLegacyTokenStorage();
    this.jwtToken = this.storageService.get<string>(STORAGE_TYPE.SESSION, this.JWT_TOKEN) ?? '';
    this.decodeToken();
  }

  /**
   * Move access tokens legados de localStorage → sessionStorage sem invalidar sessões ativas.
   */
  private migrateLegacyTokenStorage(): void {
    const sessionToken: string | null = this.storageService.get<string>(STORAGE_TYPE.SESSION, this.JWT_TOKEN);
    if (sessionToken) {
      return;
    }
    const legacyToken: string | null = this.storageService.get<string>(STORAGE_TYPE.LOCAL, this.JWT_TOKEN);
    if (!legacyToken) {
      return;
    }
    this.storageService.set(STORAGE_TYPE.SESSION, this.JWT_TOKEN, legacyToken);
    this.storageService.remove(STORAGE_TYPE.LOCAL, this.JWT_TOKEN);
  }

  private decodeToken(): void {
    this.jwtTokenDecoded = this.jwtToken ? this.jwtDecode(this.jwtToken) : undefined;
  }

  public clearToken(): void {
    this.storageService.remove(STORAGE_TYPE.SESSION, this.JWT_TOKEN);
    this.storageService.remove(STORAGE_TYPE.LOCAL, this.JWT_TOKEN);
    this.storageService.remove(STORAGE_TYPE.LOCAL, this.JWT_REFRESH_TOKEN);
    this.jwtToken = '';
    this.jwtTokenDecoded = undefined;
  }

  public isExpired(): boolean {
    this.getToken();
    return helper.isTokenExpired(this.jwtToken);
  }

  public getExpirationDate(): Date | null {
    this.getToken();
    return this.jwtToken ? helper.getTokenExpirationDate(this.jwtToken) : null;
  }

  private jwtDecode(value: string): { [key: string]: string } {
    return helper.decodeToken(value);
  }
}
